import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Package,
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Truck,
  Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ScratchCardsPage = () => {
  const { isAdmin, isManager } = useAuth();
  const { stores, selectedStore } = useStore();
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [denominations, setDenominations] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [stockCounts, setStockCounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('stock');
  
  // Dialog states
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [stockCountDialogOpen, setStockCountDialogOpen] = useState(false);
  const [viewCountDialogOpen, setViewCountDialogOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isInitialStock, setIsInitialStock] = useState(false); // For first-time stock entry without pack validation
  const [varianceConfirmDialogOpen, setVarianceConfirmDialogOpen] = useState(false); // For variance warning popup
  const [storeDetails, setStoreDetails] = useState(null); // For email notifications
  
  // Delivery form
  const [deliveryForm, setDeliveryForm] = useState({
    date: new Date(),
    items: [],
    packNumbers: {} // {denominationId: ['1021-123456789', ...]}
  });
  
  // Stock count form
  const [stockCountForm, setStockCountForm] = useState({
    date: new Date(),
    entries: [],
    variance_reason: ''
  });

  useEffect(() => {
    if (stores.length > 0) {
      const storeId = selectedStore?.id || stores[0].id;
      setSelectedStoreId(storeId);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchDenominations(selectedStoreId);
      fetchStockData();
      fetchDeliveries();
      fetchStockCounts();
      fetchStoreDetails();
    }
  }, [selectedStoreId]);

  const fetchStoreDetails = async () => {
    if (!selectedStoreId) return;
    try {
      const response = await axios.get(`${API}/stores/${selectedStoreId}`);
      setStoreDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch store details');
    }
  };

  const fetchDenominations = async (storeId = selectedStoreId) => {
    if (!storeId) return;
    try {
      const response = await axios.get(`${API}/settings/scratch-card-denominations`, {
        params: { store_id: storeId }
      });
      // Sort by value ascending and filter active only
      const sorted = response.data
        .filter(d => d.is_active)
        .sort((a, b) => (a.value || 0) - (b.value || 0));
      setDenominations(sorted);
    } catch (error) {
      console.error('Failed to load denominations');
    }
  };

  const fetchStockData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/scratch-cards/items`, {
        params: { store_id: selectedStoreId }
      });
      setStockData(response.data);
    } catch (error) {
      console.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/scratch-cards/settlements`, {
        params: { store_id: selectedStoreId }
      });
      setDeliveries(response.data);
    } catch (error) {
      console.error('Failed to load settlements');
    }
  };

  const fetchStockCounts = async () => {
    try {
      const response = await axios.get(`${API}/scratch-cards/stock-counts`, {
        params: { store_id: selectedStoreId }
      });
      setStockCounts(response.data);
    } catch (error) {
      console.error('Failed to load stock counts');
    }
  };

  const initializeDeliveryForm = (initialStock = false) => {
    setIsInitialStock(initialStock);
    setDeliveryForm({
      date: new Date(),
      items: denominations.map(d => ({
        denomination_id: d.id,
        denomination_name: d.name,
        denomination_value: d.value,
        quantity: 0
      }))
    });
    setDeliveryDialogOpen(true);
  };

  const initializeFirstStockForm = () => {
    // Called when "Add First Stock" is clicked - skips pack validation
    initializeDeliveryForm(true);
  };

  const initializeStockCountForm = () => {
    // Get current stock for each denomination
    const entries = denominations.map(d => {
      const existing = stockData.find(s => s.name === d.name || s.denomination_id === d.id);
      return {
        denomination_id: d.id,
        denomination_name: d.name,
        denomination_value: d.value,
        book_stock: existing?.current_stock || 0,
        counted_stock: existing?.current_stock || 0,
        variance: 0
      };
    });
    setStockCountForm({
      date: new Date(),
      entries,
      variance_reason: ''
    });
    setStockCountDialogOpen(true);
  };

  const updateDeliveryQuantity = (denominationId, quantity) => {
    setDeliveryForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.denomination_id === denominationId 
          ? { ...item, quantity: parseInt(quantity) || 0 }
          : item
      )
    }));
  };

  const updateCountedStock = (denominationId, counted) => {
    setStockCountForm(prev => ({
      ...prev,
      entries: prev.entries.map(entry => {
        if (entry.denomination_id === denominationId) {
          const countedValue = parseInt(counted) || 0;
          return {
            ...entry,
            counted_stock: countedValue,
            variance: countedValue - entry.book_stock
          };
        }
        return entry;
      })
    }));
  };

  const handleDeliverySubmit = async () => {
    setSaving(true);
    try {
      // For each denomination with quantity > 0, add to stock
      const itemsWithQty = deliveryForm.items.filter(item => item.quantity > 0);
      
      if (itemsWithQty.length === 0) {
        toast.error('Please enter at least one quantity');
        setSaving(false);
        return;
      }

      // Pack size validation - SKIP for initial stock entry
      if (!isInitialStock) {
        const invalidPackSizes = itemsWithQty.filter(item => {
          const denom = denominations.find(d => d.id === item.denomination_id);
          const packSize = denom?.pack_size || 100;
          return item.quantity % packSize !== 0;
        });

        if (invalidPackSizes.length > 0) {
          const errorMsg = invalidPackSizes.map(item => {
            const denom = denominations.find(d => d.id === item.denomination_id);
            const packSize = denom?.pack_size || 100;
            return `${item.denomination_name}: ${item.quantity} (must be multiple of ${packSize})`;
          }).join(', ');
          toast.error(`Invalid pack sizes! Please correct: ${errorMsg}`);
          setSaving(false);
          return;
        }
      }

      for (const item of itemsWithQty) {
        // Check if item exists for this store, create if not
        let existingItem = stockData.find(s => 
          s.denomination_id === item.denomination_id || s.name === item.denomination_name
        );
        
        if (!existingItem) {
          // Create new stock item
          const newItem = await axios.post(`${API}/scratch-cards/items`, {
            store_id: selectedStoreId,
            name: item.denomination_name,
            price: item.denomination_value,
            initial_stock: 0,
            denomination_id: item.denomination_id
          });
          existingItem = newItem.data;
        }
        
        // Create settlement record (adds to stock automatically)
        // Parse pack numbers from comma-separated string to array
        const packNumbersRaw = deliveryForm.packNumbers?.[item.denomination_id] || '';
        const packNumbersArray = packNumbersRaw
          .split(',')
          .map(p => p.trim())
          .filter(p => p.length > 0);
        
        await axios.post(`${API}/scratch-cards/settlements`, {
          store_id: selectedStoreId,
          settled_date: format(deliveryForm.date, 'yyyy-MM-dd'),
          denomination_id: item.denomination_id,
          denomination_name: item.denomination_name,
          denomination_value: item.denomination_value,
          quantity: item.quantity,
          pack_numbers: isInitialStock ? [] : packNumbersArray, // No pack numbers for initial stock
          source: isInitialStock ? 'initial_stock' : 'manual'
        });
      }
      
      toast.success(isInitialStock ? 'Initial stock recorded successfully!' : 'Card settlement recorded successfully!');
      setDeliveryDialogOpen(false);
      setIsInitialStock(false); // Reset flag
      fetchStockData();
      fetchDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record settlement');
    } finally {
      setSaving(false);
    }
  };

  const handleStockCountSubmit = async () => {
    // Check if there's any variance
    const hasVariance = stockCountForm.entries.some(e => e.variance !== 0);
    
    if (hasVariance && !stockCountForm.variance_reason.trim()) {
      toast.error('Please provide a reason for the variance');
      return;
    }
    
    // If there's variance and confirmation dialog hasn't been shown yet, show it
    if (hasVariance && !varianceConfirmDialogOpen) {
      setVarianceConfirmDialogOpen(true);
      return;
    }
    
    // Proceed with submission
    await submitStockCount();
  };

  const submitStockCount = async () => {
    const hasVariance = stockCountForm.entries.some(e => e.variance !== 0);
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/scratch-cards/stock-count`, {
        store_id: selectedStoreId,
        count_date: format(stockCountForm.date, 'yyyy-MM-dd'),
        variance_reason: stockCountForm.variance_reason,
        entries: stockCountForm.entries.map(e => {
          const existingItem = stockData.find(s => s.denomination_id === e.denomination_id || s.name === e.denomination_name);
          return {
            item_id: existingItem?.id || null,
            item_name: e.denomination_name,
            denomination_id: e.denomination_id,
            denomination_value: e.denomination_value,
            book_stock: e.book_stock,
            counted_stock: e.counted_stock,
            variance: e.variance
          };
        }) // Include all entries, even those without existing items (for opening stock)
      });
      
      // Auto-approve if manager/admin
      if (isAdmin || isManager) {
        await axios.post(`${API}/scratch-cards/stock-counts/${response.data.id}/approve`, {});
      }
      
      toast.success('Stock count recorded successfully!');
      setStockCountDialogOpen(false);
      setVarianceConfirmDialogOpen(false);
      
      // Send variance email notification if there's variance and store has email
      if (hasVariance && storeDetails?.email) {
        try {
          // Build variance details for entries with variance
          const varianceDetails = stockCountForm.entries
            .filter(e => e.variance !== 0)
            .map(e => ({
              name: e.denomination_name,
              value: e.denomination_value || 0,
              opening: e.book_stock,
              settled: 0, // Stock count doesn't have settled
              closing: e.counted_stock,
              variance: e.variance
            }));
          
          await axios.post(`${API}/notifications/scratch-card-variance`, {
            store_id: selectedStoreId,
            store_name: storeDetails.name,
            store_email: storeDetails.email,
            entry_date: format(stockCountForm.date, 'yyyy-MM-dd'),
            shift: 'Stock Count',
            variance_details: varianceDetails,
            variance_reason: stockCountForm.variance_reason
          });
          toast.info('Scratch card variance notification sent to store email');
        } catch (error) {
          console.error('Failed to send scratch card variance notification:', error);
          // Don't fail the submission if email fails
        }
      }
      
      fetchStockData();
      fetchStockCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record stock count');
    } finally {
      setSaving(false);
    }
  };

  const getTotalStockValue = () => {
    return stockData.reduce((sum, item) => sum + (item.current_stock * item.price), 0);
  };

  const getLowStockItems = () => {
    return stockData.filter(item => item.current_stock < 10);
  };

  return (
    <div className="space-y-6" data-testid="scratch-cards-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Scratch Card Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Track scratch card stock and sales
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
            <SelectTrigger className="w-[200px]" data-testid="store-select">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Stock Value</p>
                <p className="text-2xl font-bold font-mono">£{getTotalStockValue().toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-primary/20">
                <Package className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Card Types</p>
                <p className="text-2xl font-bold">{stockData.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-green-500/20">
                <TrendingUp className="h-6 w-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Low Stock Alerts</p>
                <p className="text-2xl font-bold">{getLowStockItems().length}</p>
              </div>
              <div className={`p-3 rounded-xl ${getLowStockItems().length > 0 ? 'bg-red-500/20' : 'bg-muted'}`}>
                <AlertTriangle className={`h-6 w-6 ${getLowStockItems().length > 0 ? 'text-red-400' : 'text-muted-foreground'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="stock" data-testid="tab-stock">Current Stock</TabsTrigger>
            <TabsTrigger value="deliveries" data-testid="tab-deliveries">Settlements</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Stock Counts</TabsTrigger>
          </TabsList>
          
          <div className="flex gap-2">
            <Button onClick={() => initializeDeliveryForm(false)} data-testid="add-delivery-btn">
              <Plus className="h-4 w-4 mr-2" />
              Card Settled
            </Button>
            {(isAdmin || isManager) && (
              <Button variant="outline" onClick={initializeStockCountForm} data-testid="stock-count-btn">
                <Package className="h-4 w-4 mr-2" />
                Stock Count
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="stock" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : stockData.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <Package className="h-12 w-12 mb-4 opacity-50" />
                <p>No stock data available</p>
                <Button variant="outline" className="mt-4" onClick={initializeFirstStockForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Stock
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stockData.map((item) => (
                <Card key={item.id} className={`card-hover ${item.current_stock < 10 ? 'border-red-500/50' : ''}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-xl bg-primary/20">
                          <Package className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{item.name}</h3>
                          <p className="text-sm text-muted-foreground">£{item.price} each</p>
                        </div>
                      </div>
                      {item.current_stock < 10 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">
                          Low Stock
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Current Stock</span>
                        <span className="font-mono font-semibold">{item.current_stock}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Stock Value</span>
                        <span className="font-mono">£{(item.current_stock * item.price).toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-4 font-medium text-muted-foreground">ID</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Settled Date</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Card Value</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Quantity</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Pack Numbers</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center p-8 text-muted-foreground">
                          No settlement records found
                        </td>
                      </tr>
                    ) : (
                      deliveries.map((settlement) => (
                        <tr key={settlement.id} className="border-b border-border/50 table-row-hover">
                          <td className="p-4 font-mono text-xs text-primary">
                            {settlement.transaction_id || '-'}
                          </td>
                          <td className="p-4">{new Date(settlement.settled_date).toLocaleDateString('en-GB')}</td>
                          <td className="p-4">
                            <span className="font-medium">{settlement.denomination_name || '-'}</span>
                            <span className="text-muted-foreground ml-1">(£{settlement.denomination_value || 0})</span>
                          </td>
                          <td className="p-4 text-right font-mono">{settlement.quantity}</td>
                          <td className="p-4">
                            {settlement.pack_numbers && settlement.pack_numbers.length > 0 ? (
                              <div className="text-xs font-mono space-y-1">
                                {settlement.pack_numbers.map((pn, idx) => (
                                  <div key={idx} className="bg-muted/50 px-1.5 py-0.5 rounded inline-block mr-1">{pn}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </td>
                          <td className="p-4 text-sm">{settlement.settled_by_name || 'Unknown'}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              settlement.source === 'sales_entry' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {settlement.source === 'sales_entry' ? 'Sales Entry' : 'Manual'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-4 font-medium text-muted-foreground">ID</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Counted By</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Variance (Cards)</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Variance (Amount)</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Reason</th>
                      <th className="text-right p-4 font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockCounts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center p-8 text-muted-foreground">
                          No stock count records found
                        </td>
                      </tr>
                    ) : (
                      stockCounts.map((count) => (
                        <tr 
                          key={count.id} 
                          className="border-b border-border/50 table-row-hover cursor-pointer"
                          onClick={() => {
                            setSelectedCount(count);
                            setViewCountDialogOpen(true);
                          }}
                          data-testid={`stock-count-row-${count.id}`}
                        >
                          <td className="p-4 font-mono text-xs text-primary">
                            {count.transaction_id || '-'}
                          </td>
                          <td className="p-4">{new Date(count.count_date).toLocaleDateString('en-GB')}</td>
                          <td className="p-4">{count.counted_by_name || 'Unknown'}</td>
                          <td className="p-4 text-right font-mono">
                            {count.total_variance !== 0 ? (
                              <span className={count.total_variance > 0 ? 'text-green-400' : 'text-red-400'}>
                                {count.total_variance > 0 ? '+' : ''}{count.total_variance}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </td>
                          <td className="p-4 text-right font-mono">
                            {count.total_variance_amount && count.total_variance_amount !== 0 ? (
                              <span className={count.total_variance_amount > 0 ? 'text-green-400' : 'text-red-400'}>
                                {count.total_variance_amount > 0 ? '+' : '-'}£{Math.abs(count.total_variance_amount || 0).toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">£0.00</span>
                            )}
                          </td>
                          <td className="p-4 text-sm max-w-xs truncate">
                            {count.variance_reason || '-'}
                          </td>
                          <td className="p-4 text-right">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              count.is_approved ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {count.is_approved ? 'Approved' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Card Settled Dialog */}
      <Dialog open={deliveryDialogOpen} onOpenChange={(open) => {
        setDeliveryDialogOpen(open);
        if (!open) setIsInitialStock(false); // Reset flag when closing
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {isInitialStock ? 'Add Initial Stock' : 'Card Settled'}
            </DialogTitle>
            {isInitialStock && (
              <p className="text-sm text-muted-foreground mt-1">
                Enter your existing scratch card stock. Pack size validation is not required for initial stock entry.
              </p>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isInitialStock ? 'Stock Date' : 'Settled Date'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(deliveryForm.date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={deliveryForm.date}
                      onSelect={(date) => date && setDeliveryForm({ ...deliveryForm, date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-3">
              <Label>{isInitialStock ? 'Current Stock Quantities' : 'Settled Card Quantities & Pack Numbers'}</Label>
              {!isInitialStock && (
                <p className="text-xs text-muted-foreground">Enter pack numbers in format: 1021-123456789 (comma separated for multiple packs)</p>
              )}
              {isInitialStock && (
                <p className="text-xs text-muted-foreground">Enter your current stock quantity for each card type (any quantity allowed)</p>
              )}
              {deliveryForm.items.map((item) => {
                const denom = denominations.find(d => d.id === item.denomination_id);
                const packSize = denom?.pack_size || 100;
                const qty = item.quantity || 0;
                const isInvalidPack = !isInitialStock && qty > 0 && qty % packSize !== 0;
                const packNumbers = deliveryForm.packNumbers?.[item.denomination_id] || '';
                
                return (
                  <div key={item.denomination_id} className={`p-3 border rounded-lg space-y-2 ${isInvalidPack ? 'border-red-500/50 bg-red-500/10' : 'border-border'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{item.denomination_name}</span>
                        {!isInitialStock && <span className="text-xs text-muted-foreground ml-2">(pack: {packSize})</span>}
                      </div>
                      <Input
                        type="number"
                        className={`w-24 font-mono text-right ${isInvalidPack ? 'border-red-500' : ''}`}
                        value={item.quantity || ''}
                        onChange={(e) => updateDeliveryQuantity(item.denomination_id, e.target.value)}
                        placeholder="0"
                        data-testid={`qty-${item.denomination_id}`}
                      />
                    </div>
                    {/* Only show pack numbers field for regular settlements, not initial stock */}
                    {!isInitialStock && item.quantity > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs">Pack Number(s)</Label>
                        <Input
                          placeholder="e.g. 1021-123456789, 1021-987654321"
                          value={packNumbers}
                          onChange={(e) => setDeliveryForm(prev => ({
                            ...prev,
                            packNumbers: { ...prev.packNumbers, [item.denomination_id]: e.target.value }
                          }))}
                          className="font-mono text-sm"
                          data-testid={`pack-${item.denomination_id}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Pack size validation warning - only for regular settlements */}
              {!isInitialStock && deliveryForm.items.some(item => {
                const denom = denominations.find(d => d.id === item.denomination_id);
                const packSize = denom?.pack_size || 100;
                return item.quantity > 0 && item.quantity % packSize !== 0;
              }) && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 font-medium text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    Pack Size Error
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Settled quantities must be multiples of pack size. Please correct the highlighted entries.
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4 border-t border-border mt-4">
            <Button variant="outline" onClick={() => {
              setDeliveryDialogOpen(false);
              setIsInitialStock(false);
            }}>Cancel</Button>
            <Button 
              onClick={handleDeliverySubmit} 
              disabled={saving || (!isInitialStock && deliveryForm.items.some(item => {
                const denom = denominations.find(d => d.id === item.denomination_id);
                const packSize = denom?.pack_size || 100;
                return item.quantity > 0 && item.quantity % packSize !== 0;
              }))} 
              data-testid="save-delivery-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isInitialStock ? 'Save Initial Stock' : 'Save Settlement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Count Dialog */}
      <Dialog open={stockCountDialogOpen} onOpenChange={setStockCountDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>Stock Count</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div className="space-y-2">
              <Label>Count Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(stockCountForm.date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={stockCountForm.date}
                    onSelect={(date) => date && setStockCountForm({ ...stockCountForm, date })}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-5 gap-2 text-sm font-medium text-muted-foreground px-3">
                <span>Card Type</span>
                <span className="text-right">Book</span>
                <span className="text-right">Counted</span>
                <span className="text-right">Variance</span>
                <span className="text-right">Amount</span>
              </div>
              {stockCountForm.entries.map((entry) => {
                const varianceAmount = entry.variance * (entry.denomination_value || 0);
                return (
                  <div key={entry.denomination_id} className={`grid grid-cols-5 gap-2 items-center p-3 border rounded-lg ${
                    entry.variance !== 0 ? 'border-red-500/50 bg-red-500/5' : 'border-border'
                  }`}>
                    <span className="font-medium text-sm">{entry.denomination_name}</span>
                    <span className="text-right font-mono">{entry.book_stock}</span>
                    <Input
                      type="number"
                      className="w-full font-mono text-right"
                      value={entry.counted_stock}
                      onChange={(e) => updateCountedStock(entry.denomination_id, e.target.value)}
                      data-testid={`count-${entry.denomination_id}`}
                    />
                    <span className={`text-right font-mono font-semibold ${
                      entry.variance > 0 ? 'text-green-400' : entry.variance < 0 ? 'text-red-400' : ''
                    }`}>
                      {entry.variance > 0 ? '+' : ''}{entry.variance}
                    </span>
                    <span className={`text-right font-mono font-semibold ${
                      varianceAmount > 0 ? 'text-green-400' : varianceAmount < 0 ? 'text-red-400' : ''
                    }`}>
                      {varianceAmount !== 0 ? (varianceAmount > 0 ? '+' : '') + '£' + Math.abs(varianceAmount).toFixed(0) : '-'}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Variance Warning and Reason */}
            {stockCountForm.entries.some(e => e.variance !== 0) && (
              <div className="p-4 border border-red-500/50 bg-red-500/10 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Variance Detected!</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground">Total Variance Amount:</span>
                    <span className={`ml-2 font-mono font-bold ${
                      stockCountForm.entries.reduce((sum, e) => sum + (e.variance * (e.denomination_value || 0)), 0) > 0 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {(() => {
                        const total = stockCountForm.entries.reduce((sum, e) => sum + (e.variance * (e.denomination_value || 0)), 0);
                        return (total > 0 ? '+' : '') + '£' + Math.abs(total).toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  There are differences between book stock and counted stock. Please provide a reason.
                </p>
                <div className="space-y-2">
                  <Label className="text-red-400">Variance Reason *</Label>
                  <Textarea
                    value={stockCountForm.variance_reason}
                    onChange={(e) => setStockCountForm({ ...stockCountForm, variance_reason: e.target.value })}
                    placeholder="Enter reason for the variance..."
                    rows={3}
                    className="border-red-500/30"
                    data-testid="variance-reason"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t border-border mt-4">
            <Button variant="outline" onClick={() => setStockCountDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleStockCountSubmit} disabled={saving} data-testid="save-count-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Stock Count Details Dialog (Read-Only) */}
      <Dialog open={viewCountDialogOpen} onOpenChange={setViewCountDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>Stock Count Details</DialogTitle>
          </DialogHeader>

          {selectedCount && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Summary Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Date</Label>
                  <p className="font-medium">{new Date(selectedCount.count_date).toLocaleDateString('en-GB')}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Counted By</Label>
                  <p className="font-medium">{selectedCount.counted_by_name || 'Unknown'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Status</Label>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    selectedCount.is_approved ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {selectedCount.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Total Variance</Label>
                  <p className={`font-mono font-semibold ${
                    selectedCount.total_variance > 0 ? 'text-green-400' : selectedCount.total_variance < 0 ? 'text-red-400' : ''
                  }`}>
                    {selectedCount.total_variance > 0 ? '+' : ''}{selectedCount.total_variance || 0}
                  </p>
                </div>
              </div>

              {/* Variance Reason */}
              {selectedCount.variance_reason && (
                <div className="p-3 border border-border rounded-lg bg-muted/30">
                  <Label className="text-muted-foreground text-xs">Variance Reason</Label>
                  <p className="mt-1 text-sm">{selectedCount.variance_reason}</p>
                </div>
              )}

              {/* Count Entries Table */}
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs">Count Details</Label>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/30 border-b border-border">
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Card Type</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Book</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Counted</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Variance</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCount.entries?.map((entry, idx) => {
                        // Get denomination value from entry or parse from item_name
                        let denomValue = entry.denomination_value || 0;
                        if (!denomValue && entry.item_name) {
                          const match = entry.item_name.match(/£(\d+)/);
                          if (match) denomValue = parseInt(match[1]);
                        }
                        const varianceAmount = entry.variance * denomValue;
                        return (
                          <tr key={idx} className={`border-b border-border/50 ${
                            entry.variance !== 0 ? 'bg-red-500/5' : ''
                          }`}>
                            <td className="p-3 text-sm font-medium">{entry.item_name}</td>
                            <td className="p-3 text-right font-mono text-sm">{entry.book_stock}</td>
                            <td className="p-3 text-right font-mono text-sm">{entry.counted_stock}</td>
                            <td className={`p-3 text-right font-mono text-sm font-semibold ${
                              entry.variance > 0 ? 'text-green-400' : entry.variance < 0 ? 'text-red-400' : ''
                            }`}>
                              {entry.variance > 0 ? '+' : ''}{entry.variance}
                            </td>
                            <td className={`p-3 text-right font-mono text-sm font-semibold ${
                              varianceAmount > 0 ? 'text-green-400' : varianceAmount < 0 ? 'text-red-400' : ''
                            }`}>
                              {varianceAmount !== 0 ? (varianceAmount > 0 ? '+' : '') + '£' + Math.abs(varianceAmount).toFixed(0) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="pt-4 border-t border-border mt-4">
            <Button variant="outline" onClick={() => setViewCountDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variance Confirmation Dialog */}
      <Dialog open={varianceConfirmDialogOpen} onOpenChange={setVarianceConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Scratch Card Variance Detected
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              A variance has been detected between the book stock and counted stock. 
              Please double-check the figures before proceeding.
            </p>

            {/* Variance Summary */}
            <div className="p-4 border border-amber-500/30 bg-amber-500/10 rounded-lg space-y-3">
              <div className="text-sm font-medium text-amber-400">Variance Summary:</div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {stockCountForm.entries.filter(e => e.variance !== 0).map((entry) => {
                  const varianceAmount = entry.variance * (entry.denomination_value || 0);
                  return (
                    <div key={entry.denomination_id} className="flex justify-between items-center text-sm">
                      <span>{entry.denomination_name}</span>
                      <div className="flex gap-4 font-mono">
                        <span className={entry.variance > 0 ? 'text-green-400' : 'text-red-400'}>
                          {entry.variance > 0 ? '+' : ''}{entry.variance} cards
                        </span>
                        <span className={varianceAmount > 0 ? 'text-green-400' : 'text-red-400'}>
                          {varianceAmount > 0 ? '+' : ''}£{Math.abs(varianceAmount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="pt-2 border-t border-amber-500/30 flex justify-between items-center font-medium">
                <span>Total Variance:</span>
                <span className={`font-mono ${
                  stockCountForm.entries.reduce((sum, e) => sum + (e.variance * (e.denomination_value || 0)), 0) > 0 
                    ? 'text-green-400' 
                    : 'text-red-400'
                }`}>
                  {(() => {
                    const total = stockCountForm.entries.reduce((sum, e) => sum + (e.variance * (e.denomination_value || 0)), 0);
                    return (total > 0 ? '+' : '') + '£' + Math.abs(total).toFixed(2);
                  })()}
                </span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> A variance notification will be sent to the store email address after submission.
            </p>
          </div>

          <DialogFooter className="flex gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setVarianceConfirmDialogOpen(false)}
              data-testid="variance-go-back-btn"
            >
              Go Back
            </Button>
            <Button 
              onClick={submitStockCount} 
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="variance-continue-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Continue Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScratchCardsPage;
